import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import client from "@/lib/api";

const SiteContentCtx = createContext({ sections: {}, loading: true });

export function SiteContentProvider({ children }) {
  const [sections, setSections] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const r = await client.get("/site-content");
      setSections(r.data || {});
    } catch (err) {
      console.warn("Failed to load /site-content; using fallbacks", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const value = useMemo(() => ({ sections, loading, refresh }), [sections, loading]);
  return <SiteContentCtx.Provider value={value}>{children}</SiteContentCtx.Provider>;
}

/**
 * Returns merged content for a section slug: { ...section, ...section.meta, _fallback }
 * Pass `fallback` so the UI stays usable while content is loading or if the slug is missing.
 *
 * Brand hygiene: any saved CMS image URL pointing at images.unsplash.com is
 * silently dropped so the in-code fallback (brand product photo) takes over.
 * Keeps the public site free of stock fashion/bike photography even if older
 * CMS rows haven't been re-seeded.
 */
function _scrubStockImageUrl(value) {
  if (typeof value !== "string") return value;
  if (value.includes("images.unsplash.com")) return "";
  return value;
}

export function useSection(slug, fallback = {}) {
  const { sections } = useContext(SiteContentCtx);
  const section = sections[slug];
  if (!section) return { ...fallback };
  const meta = section.meta || {};
  // meta keys override top-level so admin can edit either place
  const merged = { ...fallback, ...section, ...meta };
  // Drop any saved Unsplash image references — fallback fields take precedence.
  for (const key of Object.keys(merged)) {
    if (/image|hero|featured|photo/i.test(key)) {
      const cleaned = _scrubStockImageUrl(merged[key]);
      if (!cleaned && fallback[key]) merged[key] = fallback[key];
      else merged[key] = cleaned || merged[key];
    }
  }
  return merged;
}

export const useSiteContent = () => useContext(SiteContentCtx);
